// "All workspaces": the same message search asked once per workspace the person is in, in
// parallel under a cap. Each workspace's relay decides what it shows (private channels and other
// people's DMs never leave it), so nothing is filtered here; a workspace that fails or refuses
// costs only its own group.
import type { Message } from "$lib/core/types.js";
import type { SearchResultChannel } from "$lib/message-search-result.js";
import { reportSearchLegFailure } from "./global-search.js";

/** Workspaces asked at once: enough to feel instant, few enough not to flood one relay socket. */
export const ALL_WORKSPACES_CONCURRENCY = 4;
/** Hits asked of each workspace. The panel previews a few and expands on demand. */
export const ALL_WORKSPACES_LIMIT = 25;
/** Rows each workspace shows before "Show N more", so one busy workspace cannot hide the rest. */
export const ALL_WORKSPACES_PREVIEW = 3;

export interface WorkspaceRef {
  id: string;
  name: string;
}

export interface AllWorkspacesDeps {
  search(workspaceId: string): Promise<Message[]>;
  /** The person's channels in a workspace, for the names of its hits. */
  channels(workspaceId: string): Promise<Array<SearchResultChannel & { id: string }>>;
  /** The people of a workspace (members and guests), to name and route its DM hits. */
  people(workspaceId: string): Promise<Array<{ id: string; name: string }>>;
  /** The agents of a workspace, to name and route a DM with one (the person's own message too). */
  agents(workspaceId: string): Promise<Array<{ id: string; name: string }>>;
  /** A fixed "you are not in this workspace" answer, not a failure. */
  isRefusal(err: unknown): boolean;
  /** True once a newer search replaced this one: no further workspace is asked. */
  stale?(): boolean;
}

export interface AllWorkspacesResult {
  /** Every hit, stamped with its workspace and grouped: `currentId` first, then freshest first. */
  messages: Message[];
  /** Workspaces that could not be searched (not the ones that refused or found nothing). */
  failed: WorkspaceRef[];
  /** Channel names of the other workspaces' hits: workspaceId -> channelId -> channel. */
  channels: Map<string, Map<string, SearchResultChannel>>;
  /** People of the other workspaces that have DM hits: workspaceId -> userId -> name. */
  people: Map<string, Map<string, string>>;
  /** Agents of the other workspaces that have DM hits: workspaceId -> agent id -> name. */
  agents: Map<string, Map<string, string>>;
}

async function pooled<T>(
  items: T[],
  limit: number,
  stale: () => boolean,
  work: (item: T) => Promise<void>,
) {
  let next = 0;
  const lane = async (): Promise<void> => {
    while (next < items.length && !stale()) await work(items[next++]);
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
}

// A roster that cannot be read is not a reason to lose the hits: they keep a generic name.
async function soft<V>(workspaceId: string, load: () => Promise<Array<[string, V]>>) {
  try {
    return new Map(await load());
  } catch (err) {
    // intentional: reported; the hits keep their place under a generic name.
    reportSearchLegFailure("messages", err, workspaceId);
    return new Map<string, V>();
  }
}

/** The first rows of each workspace's group, in order: what shows before "Show N more". */
export function previewPerWorkspace(rows: Message[], each = ALL_WORKSPACES_PREVIEW): Message[] {
  const shown = new Map<string | undefined, number>();
  return rows.filter((m) => {
    const n = shown.get(m.workspaceId) ?? 0;
    shown.set(m.workspaceId, n + 1);
    return n < each;
  });
}

const newest = (rows: Message[]): number => Math.max(...rows.map((m) => m.createdAt));

export async function searchAllWorkspaces(
  workspaces: WorkspaceRef[],
  currentId: string | null,
  deps: AllWorkspacesDeps,
): Promise<AllWorkspacesResult> {
  const hits = new Map<string, Message[]>();
  const failed: WorkspaceRef[] = [];
  const channels = new Map<string, Map<string, SearchResultChannel>>();
  const people = new Map<string, Map<string, string>>();
  const agents = new Map<string, Map<string, string>>();
  const stale = () => deps.stale?.() ?? false;
  await pooled(workspaces, ALL_WORKSPACES_CONCURRENCY, stale, async (ws) => {
    try {
      const rows = await deps.search(ws.id);
      for (const m of rows) m.workspaceId = ws.id; // the relay's row may not carry it
      if (rows.length === 0) return;
      hits.set(ws.id, rows);
      if (ws.id === currentId) return;
      const dms = rows.some((m) => !m.channelId);
      const [named, known, bots] = await Promise.all([
        rows.some((m) => m.channelId)
          ? soft(ws.id, async () =>
              (await deps.channels(ws.id)).map((c): [string, SearchResultChannel] => [c.id, c]),
            )
          : undefined,
        dms
          ? soft(ws.id, async () =>
              (await deps.people(ws.id)).map((p): [string, string] => [p.id, p.name]),
            )
          : undefined,
        dms
          ? soft(ws.id, async () =>
              (await deps.agents(ws.id)).map((a): [string, string] => [a.id, a.name]),
            )
          : undefined,
      ]);
      if (bots) agents.set(ws.id, bots);
      if (named) channels.set(ws.id, named);
      if (known) people.set(ws.id, known);
    } catch (err) {
      // intentional: a refusal is that workspace's fixed answer; a failure is reported and listed.
      if (deps.isRefusal(err)) return;
      reportSearchLegFailure("messages", err, ws.id);
      failed.push(ws);
    }
  });
  const order = [...hits.keys()].sort((a, b) => {
    if (a === currentId) return -1;
    if (b === currentId) return 1;
    return newest(hits.get(b) ?? []) - newest(hits.get(a) ?? []);
  });
  return { messages: order.flatMap((id) => hits.get(id) ?? []), failed, channels, people, agents };
}
